import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import Spinner from "../../components/ui/Spinner";
import MessageBox from "../../components/ui/MessageBox";
import Button from "../../components/ui/Button";
import StockDetailsPage from "./StockDetailsPage";
import AddStockPage from "./AddStockPage";
import Heading from "../../components/ui/Heading"; 
import SearchInput from "../../components/ui/SearchInput";
import { PlusCircle, FileEdit  } from "lucide-react";

const StockCard = ({ stock, onEdit, onInvoice }) => {
  return (
    <div className="p-4 transition-shadow bg-white border border-gray-200 shadow rounded-xl hover:shadow-lg">
      <h3 className="text-base font-semibold text-gray-900">
        Doc No: {stock.DocNo}
      </h3>
      <div className="mt-1 text-sm text-gray-700">
        {stock.DocDate ? new Date(stock.DocDate).toLocaleDateString() : ""}
      </div>
      <div className="text-sm text-gray-700">{stock.Reason || "-"}</div>
      <div className="mt-1 text-sm text-gray-700">
        Qty: {stock.TotalQty} | Value: {stock.TotalValue}
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button
          className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"
          onClick={onEdit}
        > <FileEdit className="w-5 h-5" />
          Edit
        </Button>
        {/* Uncomment if you want Stock Movement button
        <Button
          className="px-4 py-2 text-white bg-green-500 rounded-lg shadow-sm hover:bg-green-600"
          onClick={onInvoice}
        >
          Stock Movement
        </Button>
        */}
      </div>
    </div>
  );
};

const StockListPage = () => {
  const { user } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [view, setView] = useState("list"); // list | add-stock | edit-stock
  const [viewMode, setViewMode] = useState("card"); // card | table
  const [editingContext, setEditingContext] = useState(null);

  const fetchStocks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getsimplestockList(user.UsrName);

      const list = (data?.Table || []).map((o) => ({
        Poid: o.Poid,
        DocNo: String(o.Poid),
        DocDate: o.Podate,
        Reason: o.Vname || "-",
        TotalQty: o.SumQty?.replace("Qty :", "").trim() || "0",
        TotalValue: o.OrderTotal?.replace("Ord Rs:", "").trim() || "0",
        ...o,
      }));

      setStocks(list);
      setFilteredStocks(list);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch stock list.");
    } finally {
      setLoading(false);
    }
  }, [user.UsrName]);

  useEffect(() => {
    fetchStocks();
  }, [fetchStocks]);

  useEffect(() => {
    const term = (searchTerm || "").toLowerCase();
    const results = stocks.filter(
      (o) =>
        (o.DocNo && o.DocNo.toLowerCase().includes(term)) ||
        (o.Reason && o.Reason.toLowerCase().includes(term))
    );
    setFilteredStocks(results);
  }, [searchTerm, stocks]);

  // --- Edit button handler ---
  const handleEdit = async (poid) => {
    setLoading(true);
    try {
      const data = await api.editpoStock(poid);
      const stockData = data?.Table?.[0] || {};
      const orderItems = data?.Table1 || [];
      const modes = data?.Table2 || [];

      setEditingContext({
        id: poid,
        stockId: stockData.Vid,
        orderDate: stockData.podate || stockData.StockDate,
        stock: stockData,
        items: orderItems,
        modes,
      });
      setView("edit-stock");
    } catch (err) {
      console.error(err);
      setError("Failed to fetch stock details for editing.");
    } finally {
      setLoading(false);
    }
  };

  // --- Stock Movement button handler ---
  const handleInvoice = async (poid) => {
    setLoading(true);
    setError(""); 
    try {
      const response = await api.StockAdjustment(poid, user.UsrName);
      console.log("Stock Adjustment API response:", response);

      const msg = response?.Table?.[0]?.StockMsg || "No stock adjustment message returned.";
      alert(msg);
    } catch (err) {
      console.error(err);
      alert("Failed to process stock adjustment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // --- Conditional Rendering for Edit/Add Stock Pages ---
  if (view === "edit-stock" && editingContext) {
    return (
      <StockDetailsPage
        context={editingContext}
        setView={setView}
        refreshOrders={fetchStocks}
        user={user}
      />
    );
  }

  if (view === "add-stock") {
    return (
      <AddStockPage
        setView={setView}
        setEditingContext={setEditingContext}
        refreshOrders={fetchStocks}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header Section */}
      <div className="px-3 py-3 bg-gray-100 border-b">
        <div className="mb-3">
          <Heading>Stock Adjustment</Heading>
        </div>

        <div className="flex items-center gap-2 mb-3">
          {/* New Button */}
          <Button
            className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
              rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
            onClick={() => setView("add-stock")}
          >
            <PlusCircle size={18} />
            <span className="font-medium">New</span>
          </Button>

          <div className="flex-1" />

          {/* Grid / Table Toggle */}
          <div className="flex items-center justify-center p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setViewMode("card")}
              className={`p-2 rounded ${viewMode === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded ${viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Back + Search */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
            onClick={() => setView("list")}
          >
            ⬅
          </Button>

          <SearchInput
            placeholder="Search by document no or reason..."
            className="flex-1"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Body Section */}
      <div className="p-3 flex-1 overflow-auto">
        {loading ? (
          <Spinner />
        ) : error ? (
          <MessageBox message={error} />
        ) : filteredStocks.length === 0 ? (
          <div className="py-12 text-center text-gray-500">No stock adjustment found.</div>
        ) : viewMode === "table" ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-900 uppercase">Doc No</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-900 uppercase">Date</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-900 uppercase">Reason</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-900 uppercase">Qty</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-900 uppercase">Value</th>
                  <th className="px-4 py-2 text-center text-sm font-medium text-gray-900 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStocks.map((stock) => (
                  <tr key={stock.Poid}>
                    <td className="px-4 py-2">{stock.DocNo}</td>
                    <td className="px-4 py-2">{stock.DocDate ? new Date(stock.DocDate).toLocaleDateString() : ""}</td>
                    <td className="px-4 py-2">{stock.Reason}</td>
                    <td className="px-4 py-2 text-right">{stock.TotalQty}</td>
                    <td className="px-4 py-2 text-right">{stock.TotalValue}</td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button onClick={() => handleEdit(stock.Poid)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md">
                          <FileEdit className="w-4 h-4"/> Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredStocks.map((stock) => (
              <StockCard
                key={stock.Poid}
                stock={stock}
                onEdit={() => handleEdit(stock.Poid)}
                onInvoice={() => handleInvoice(stock.Poid)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StockListPage;


// import React, { useEffect, useState, useCallback } from "react";
// import { useAuth } from "../../context/AuthContext";
// import api from "../../api/api";
// import Spinner from "../../components/ui/Spinner";
// import MessageBox from "../../components/ui/MessageBox";
// import Button from "../../components/ui/Button";
// import StockDetailsPage from "./StockDetailsPage";
// import AddStockPage from "./AddStockPage";
// import Heading from "../../components/ui/Heading"; 
// import SearchInput from "../../components/ui/SearchInput";
// import { PlusCircle, FileEdit  } from "lucide-react";


// const StockCard = ({ stock, onEdit, onInvoice }) => {
//   return (
//     <div className="p-4 transition-shadow bg-white border border-gray-200 shadow rounded-xl hover:shadow-lg">
//       <h3 className="text-base font-semibold text-gray-900">
//         Doc No: {stock.DocNo}
//       </h3>
//       <div className="mt-1 text-sm text-gray-700">
//         {stock.DocDate ? new Date(stock.DocDate).toLocaleDateString() : ""}
//       </div>
//       <div className="text-sm text-gray-700">{stock.Reason || "-"}</div>
//       <div className="mt-1 text-sm text-gray-700">
//         Qty: {stock.TotalQty} | Value: {stock.TotalValue}
//       </div>
//       <div className="flex flex-wrap gap-2 mt-4">
//         <Button
//           className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
//              bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
//              hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
//              transition-all duration-300 ease-in-out"
//           onClick={onEdit}
//         > <FileEdit className="w-5 h-5" />
//           Edit
//         </Button>
//         {/* <Button
//           className="px-4 py-2 text-white bg-green-500 rounded-lg shadow-sm hover:bg-green-600"
//           onClick={onInvoice}
//         >
//           Stock Movement
//         </Button> */}
//       </div>
//     </div>
//   );
// };

// const StockListPage = () => {
//   const { user } = useAuth();
//   const [stocks, setStocks] = useState([]);
//   const [filteredStocks, setFilteredStocks] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [searchTerm, setSearchTerm] = useState("");

//   const [view, setView] = useState("list"); // list | add-stock | edit-stock
//   const [viewMode, setViewMode] = useState("card");
//   const [editingContext, setEditingContext] = useState(null);

//   const fetchStocks = useCallback(async () => {
//     setLoading(true);
//     try {
//       const data = await api.getsimplestockList(user.UsrName);

//       const list = (data?.Table || []).map((o) => ({
//         DocNo: String(o.Poid),
//         DocDate: o.Podate,
//         Reason: o.Vname || "-",
//         TotalQty: o.SumQty?.replace("Qty :", "").trim() || "0",
//         TotalValue: o.OrderTotal?.replace("Ord Rs:", "").trim() || "0",
//         ...o,
//       }));

//       setStocks(list);
//       setFilteredStocks(list);
//     } catch (err) {
//       console.error(err);
//       setError("Failed to fetch stock list.");
//     } finally {
//       setLoading(false);
//     }
//   }, [user.UsrName]);

//   useEffect(() => {
//     fetchStocks();
//   }, [fetchStocks]);

//   useEffect(() => {
//     const term = (searchTerm || "").toLowerCase();
//     const results = stocks.filter(
//       (o) =>
//         (o.DocNo && o.DocNo.toLowerCase().includes(term)) ||
//         (o.Reason && o.Reason.toLowerCase().includes(term))
//     );
//     setFilteredStocks(results);
//   }, [searchTerm, stocks]);

//   // --- Edit button handler ---
//   const handleEdit = async (poid) => {
//     setLoading(true);
//     try {
//       const data = await api.editpoStock(poid);
//       const stockData = data?.Table?.[0] || {};
//       const orderItems = data?.Table1 || [];
//       const modes = data?.Table2 || [];

//       setEditingContext({
//         id: poid,
//         stockId: stockData.Vid,
//         orderDate: stockData.podate || stockData.StockDate,
//         stock: stockData,
//         items: orderItems,
//         modes,
//       });
//       setView("edit-stock");
//     } catch (err) {
//       console.error(err);
//       setError("Failed to fetch stock details for editing.");
//     } finally {
//       setLoading(false);
//     }
//   };


//   // --- Stock Movement button handler ---
//   const handleInvoice = async (poid) => {
//     setLoading(true);
//     setError(""); 
//     try {
//       const response = await api.StockAdjustment(poid, user.UsrName);
//       console.log("Stock Adjustment API response:", response);

//       const msg = response?.Table?.[0]?.StockMsg || "No stock adjustment message returned.";

//       alert(msg);
//     } catch (err) {
//       console.error(err);
//       alert("Failed to process stock adjustment. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };



//   if (view === "edit-stock" && editingContext) {
//     return (
//       <StockDetailsPage
//         context={editingContext}
//         setView={setView}
//         refreshOrders={fetchStocks}
//         user={user}
//       />
//     );
//   }

//   if (view === "add-stock") {
//     return (
//       <AddStockPage
//         setView={setView}
//         setEditingContext={setEditingContext}
//         refreshOrders={fetchStocks}
//       />
//     );
//   }

//   return (
//     <div className="flex flex-col h-full">
//       {/* Header Section */}
//       <div className="px-3 py-3 bg-grey border-b">
//         {/* Row 1: Title */}
//         <div className="mb-3">
//           <Heading>Stock Adjustment</Heading>
//         </div>

//         {/* Row 2: New + Grid/Table */}
//         <div className="flex items-center gap-2 mb-3">
//           {/* New Button */}
//           <Button
//             className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
//               rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
//             onClick={() => setView("add-stock")}
//           >
//             <PlusCircle size={18} />
//             <span className="font-medium">New</span>
//           </Button>

//           <div className="flex-1" />
//           {/* Grid / Table Toggle */}
//           <div className="flex items-center justify-center p-1 bg-white-100 rounded-lg">
//             {/* Grid */}
//             <button
//               onClick={() => setViewMode("card")}
//               className={`p-2 rounded ${viewMode === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
//             >
//               <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z" />
//               </svg>
//             </button>

//             {/* Table */}
//             <button
//               onClick={() => setViewMode("table")}
//               className={`p-2 rounded ${viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
//             >
//               <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
//               </svg>
//             </button>
//           </div>
//         </div>

//         {/* Row 3: Back + Search Box */}
//         <div className="flex items-center gap-2">
//           <Button
//             variant="outline"
//             className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
//             onClick={() => setView("list")}
//           >
//             ⬅
//           </Button>

//           <SearchInput
//             placeholder="Search by document no or reason..."
//             className="flex-1"
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//           />
//         </div>
//       </div>

//       {loading ? (
//         <Spinner />
//       ) : error ? (
//         <MessageBox message={error} />
//       ) : filteredStocks.length === 0 ? (
//         <div className="py-12 text-center text-gray-500">
//           No stock adjustment found.
//         </div>
//       ) : (
//         <div className="space-y-4">
//           {filteredStocks.map((stock) => (
//             <StockCard
//               key={stock.DocNo}
//               stock={stock}
//               onEdit={() => handleEdit(stock.Poid)}
//               onInvoice={() => handleInvoice(stock.Poid)}
//             />
//           ))}
//         </div>
//       )}
//     </div>
//   );
// };

// export default StockListPage;

